// controllers/useWebPageController.js
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useParams } from 'react-router-dom';

export default function useWebPageController() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [portofolios, setPortofolios] = useState([]);
  const [categories, setCategories] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [publications, setPublications] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cvData, setCvData] = useState(null);

  const fetchWithImages = async (table, category, userId) => {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;

    return await Promise.all(
      data?.map(async (item) => {
        const { data: images } = await supabase
          .from('image')
          .select('path')
          .eq('category_id', item.id)
          .eq('category', category);

        return {
          ...item,
          images: images?.map(img => ({
            publicUrl: supabase.storage.from('document').getPublicUrl(img.path).data.publicUrl
          })) || []
        };
      }) || []
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      month: 'short',
      year: 'numeric'
    });
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
  
      // Fetch profile with social media
      const { data: profileData, error: profileError } = await supabase
        .from('profile')
        .select('*')
        .eq('username', username)
        .single();
  
      if (profileError) throw profileError;
      if (!profileData) throw new Error('User not found');
  
      let avatarUrl = '';
      const { data: imageData } = await supabase
        .from('image')
        .select('path')
        .eq('user_id', profileData.user_id)
        .eq('category', 'avatar')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
  
      if (imageData) {
        const { data: { publicUrl } } = supabase
          .storage
          .from('document')
          .getPublicUrl(imageData.path);
        avatarUrl = publicUrl;
      }
  
      // Corrected setProfile call
      setProfile({
        ...profileData,
        avatarUrl,
      });
  
      const userId = profileData.user_id;
  
      // Fetch all data
      const [
        experiencesData,
        educationsData,
        portofoliosData,
        categoriesData,
        certificatesData,
        publicationsData,
        skillsData,
        achievementsData
      ] = await Promise.all([
        fetchWithImages('experience', 'experience', userId),
        fetchWithImages('education', 'education', userId),
        fetchWithImages('portofolio', 'portofolio', userId),
        supabase.from('porto_category').select('*').eq('user_id', userId),
        supabase.from('certificate').select('*').eq('user_id', userId),
        supabase.from('publication').select('*').eq('user_id', userId),
        supabase.from('skills').select('*').eq('user_id', userId),
        supabase.from('achievement').select('*').eq('user_id', userId)
      ]);
  
      // Fetch selected CV jika ada
      if (profileData.selected_cv_id) {
        await fetchCV(userId, profileData.selected_cv_id);
      }
  
      // Format experiences
      const formattedExperiences = experiencesData.map(exp => ({
        ...exp,
        start_date: formatDate(exp.start),
        end_date: formatDate(exp.end)
      }));
  
      // Format educations
      const formattedEducations = educationsData.map(edu => ({
        ...edu,
        start_date: formatDate(edu.start),
        end_date: formatDate(edu.end)
      }));
  
      setExperiences(formattedExperiences);
      setEducations(formattedEducations);
      setPortofolios(portofoliosData);
      setCategories(categoriesData.data || []);
      setCertificates(certificatesData.data || []);
      setPublications(publicationsData.data || []);
      setSkills(skillsData.data || []);
      setAchievements(achievementsData.data || []);
  
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCV = async (userId, cvId) => {
    try {
      let query = supabase
        .from('cv')
        .select('*')
        .eq('user_id', userId);

      if (cvId) {
        query = query.eq('id', cvId); 
      } else {
        query = query.order('created_at', { ascending: false }).limit(1);
      }

      const { data, error } = await query.single();

      if (error) throw error;
      setCvData(data);
      console.log('CV Data:', data.cv_data);
      return data;
    } catch (error) {
      console.error("Error fetching CV:", error);
      setError(cvId ? 'CV Not Found' : 'Failed to Get CV Data');
      return null;
    }
  };

  useEffect(() => {
    if (username) {
      fetchAllData();
      // fetchCV(profile?.user_id);
    } else {
      setError('No username provided');
      setLoading(false);
    }
  }, [username]);

  return {
    profile,
    experiences,
    educations,
    portofolios,
    categories,
    certificates,
    publications,
    achievements,
    skills,
    cvData,
    fetchCV,
    loading,
    error
  };
}